import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLanguageContext } from '../context/LanguageContext';
import { translations } from '../translations';

// Icons
import OrganizationIcon from '@mui/icons-material/AccountTree';
import TimeIcon from '@mui/icons-material/AccessTime';
import TeamIcon from '@mui/icons-material/Groups';
import DisciplineIcon from '@mui/icons-material/Gavel';
import EmailIcon from '@mui/icons-material/Email';
import LocationIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Translate';

const PersonalInfo = () => {
  const theme = useTheme();
  const { language } = useLanguageContext();
  const t = translations[language];

  const getStrengthIcon = (iconName) => {
    switch (iconName) {
      case 'organize':
        return <OrganizationIcon />;
      case 'time':
        return <TimeIcon />;
      case 'team':
        return <TeamIcon />;
      case 'discipline':
        return <DisciplineIcon />;
      default:
        return <OrganizationIcon />;
    }
  };

  const { email, phone, location } = t.personalInfo?.contact || {};

  return (
    <Box sx={{ py: 6 }}>
      <Grid container spacing={4}>
        {/* Strengths Section */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              height: '100%',
              backgroundColor: theme.palette.background.paper,
              transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: theme.shadows[8],
              },
            }}
          >
            <Typography variant="h5" gutterBottom color="primary">
              {t.strengths.title}
            </Typography>
            <List>
              {t.strengths.items.map((strength, index) => (
                <ListItem 
                  key={strength.name}
                  sx={{
                    transition: 'transform 0.2s ease-in-out, background-color 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateX(10px)',
                      backgroundColor: theme.palette.action.hover,
                    },
                    animation: 'fadeIn 0.5s ease-out',
                    animationDelay: `${index * 0.1}s`,
                    '@keyframes fadeIn': {
                      from: {
                        opacity: 0,
                        transform: 'translateX(-20px)',
                      },
                      to: {
                        opacity: 1,
                        transform: 'translateX(0)',
                      },
                    },
                  }}
                >
                  <ListItemIcon 
                    sx={{ 
                      color: 'primary.main',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'rotate(10deg)',
                      },
                    }}
                  >
                    {getStrengthIcon(strength.icon)}
                  </ListItemIcon>
                  <ListItemText primary={strength.name} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Languages Section */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              height: '100%',
              backgroundColor: theme.palette.background.paper,
              transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: theme.shadows[8],
              },
            }}
          >
            <Typography variant="h5" gutterBottom color="primary">
              {t.languages.title}
            </Typography>
            <List>
              {t.languages.items.map((language, index) => (
                <ListItem 
                  key={language.name}
                  sx={{
                    transition: 'transform 0.2s ease-in-out, background-color 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateX(10px)',
                      backgroundColor: theme.palette.action.hover,
                    },
                    animation: 'fadeIn 0.5s ease-out',
                    animationDelay: `${index * 0.1}s`,
                    '@keyframes fadeIn': {
                      from: {
                        opacity: 0,
                        transform: 'translateX(-20px)',
                      },
                      to: {
                        opacity: 1,
                        transform: 'translateX(0)',
                      },
                    },
                  }}
                >
                  <ListItemIcon 
                    sx={{ 
                      color: 'primary.main',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'rotate(10deg)',
                      },
                    }}
                  >
                    <LanguageIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary={language.name}
                    secondary={language.level}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Contact Information */}
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={3} 
            sx={{
              p: 3,
              height: '100%',
              width: { xs: '100%', md: 'fit-content' },
              backgroundColor: theme.palette.background.paper,
              transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: theme.shadows[8],
              },
            }}
          >
            <Typography 
              variant="h5" 
              gutterBottom 
              color="primary"
            >
              {t.contactInfo.title}
            </Typography>
            <List>
              {email && (
                <ListItem 
                  sx={{
                    transition: 'transform 0.2s ease-in-out, background-color 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateX(10px)',
                      backgroundColor: theme.palette.action.hover,
                    },
                    animation: 'fadeIn 0.5s ease-out',
                    animationDelay: '0.1s',
                    '@keyframes fadeIn': {
                      from: {
                        opacity: 0,
                        transform: 'translateX(-20px)',
                      },
                      to: {
                        opacity: 1,
                        transform: 'translateX(0)',
                      },
                    },
                  }}
                >
                  <ListItemIcon 
                    sx={{ 
                      color: 'primary.main',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'rotate(10deg)',
                      },
                    }}
                  >
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary={email} 
                    primaryTypographyProps={{
                      variant: 'body1',
                      fontWeight: 'medium'
                    }}
                  />
                </ListItem>
              )}
              {phone && (
                <ListItem 
                  sx={{
                    transition: 'transform 0.2s ease-in-out, background-color 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateX(10px)',
                      backgroundColor: theme.palette.action.hover,
                    },
                    animation: 'fadeIn 0.5s ease-out',
                    animationDelay: '0.1s',
                    '@keyframes fadeIn': {
                      from: {
                        opacity: 0,
                        transform: 'translateX(-20px)',
                      },
                      to: {
                        opacity: 1,
                        transform: 'translateX(0)',
                      },
                    },
                  }}
                >
                  <ListItemIcon 
                    sx={{ 
                      color: 'primary.main',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'rotate(10deg)',
                      },
                    }}
                  >
                    <PhoneIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary={phone} 
                    primaryTypographyProps={{
                      variant: 'body1',
                      fontWeight: 'medium'
                    }}
                  />
                </ListItem>
              )}
              {location && (
                <ListItem 
                  sx={{
                    transition: 'transform 0.2s ease-in-out, background-color 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateX(10px)',
                      backgroundColor: theme.palette.action.hover,
                    },
                    animation: 'fadeIn 0.5s ease-out',
                    animationDelay: '0.1s',
                    '@keyframes fadeIn': {
                      from: {
                        opacity: 0,
                        transform: 'translateX(-20px)',
                      },
                      to: {
                        opacity: 1,
                        transform: 'translateX(0)',
                      },
                    },
                  }}
                >
                  <ListItemIcon 
                    sx={{ 
                      color: 'primary.main',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'rotate(10deg)',
                      },
                    }}
                  >
                    <LocationIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary={location} 
                    primaryTypographyProps={{
                      variant: 'body1',
                      fontWeight: 'medium'
                    }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PersonalInfo;
